import { RefObject, useCallback, useEffect, useState } from 'react'

interface PagingState {
    perPage: number
    page: number
    pageCount: number
}

/**
 * Pages for a horizontal scroller whose children are cards of one width: a page is the number of cards that are
 * fully visible, so a step scrolls exactly those cards. `.card` children are measured, other children are ignored.
 */
const useHorizontalPaging = (scrollerRef: RefObject<HTMLElement | null>, itemCount: number) => {
    const [state, setState] = useState<PagingState>({ perPage: 1, page: 0, pageCount: 1 })

    const measure = useCallback(() => {
        const scroller = scrollerRef.current
        const card = scroller?.querySelector<HTMLElement>('.card')
        if (!scroller || !card) return

        const gap = parseFloat(getComputedStyle(card.parentElement ?? scroller).columnGap) || 0
        const step = card.offsetWidth + gap
        if (!step) return

        const perPage = Math.max(1, Math.floor((scroller.clientWidth + gap) / step))
        const pageCount = Math.max(1, Math.ceil(itemCount / perPage))
        const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1
        const page = atEnd ? pageCount - 1 : Math.min(pageCount - 1, Math.round(scroller.scrollLeft / (perPage * step)))

        setState(prev => (prev.perPage === perPage && prev.page === page && prev.pageCount === pageCount ? prev : { perPage, page, pageCount }))
    }, [scrollerRef, itemCount])

    useEffect(() => {
        const scroller = scrollerRef.current
        if (!scroller) return

        measure()
        scroller.addEventListener('scroll', measure, { passive: true })
        const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(measure)
        observer?.observe(scroller)

        return () => {
            scroller.removeEventListener('scroll', measure)
            observer?.disconnect()
        }
    }, [scrollerRef, measure])

    const goToPage = useCallback((target: number) => {
        const scroller = scrollerRef.current
        const card = scroller?.querySelector<HTMLElement>('.card')
        if (!scroller || !card) return

        const gap = parseFloat(getComputedStyle(card.parentElement ?? scroller).columnGap) || 0
        const page = Math.max(0, Math.min(state.pageCount - 1, target))
        scroller.scrollTo?.({ left: page * state.perPage * (card.offsetWidth + gap), behavior: 'smooth' })
    }, [scrollerRef, state.pageCount, state.perPage])

    return { page: state.page, pageCount: state.pageCount, goToPage }
}

export default useHorizontalPaging
